import _ from 'lodash';
import React from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { createZustandStorage } from '#/lib/storage';

export type LayoutExplorerPanelType = 'explorer';

export type LayoutUtilityPanelType = 'chat';

export type UseLayoutResult = [LayoutStoreState, LayoutStoreActions];

// track the width of the panels in pixels AND the ratio
// explorerPanelRatio = 32;
// explorerPanelWidth = 3123; // px

/**
 * A hook that returns the state and actions of the layout store.
 * @returns The state and actions of the layout store.
 */
export function useLayout(): UseLayoutResult {
  const store = useStore(useShallow((s) => s));

  const state = React.useMemo(
    () => ({
      explorerPanelOpen: store.explorerPanelOpen,
      explorerPanelType: store.explorerPanelType,
      explorerPanelWidth: store.explorerPanelWidth,
      utilityPanelOpen: store.utilityPanelOpen,
      utilityPanelType: store.utilityPanelType,
      utilityPanelWidth: store.utilityPanelWidth,
    }),
    [store]
  );

  const actions = React.useMemo(
    () => ({
      closeExplorerPanel: store.closeExplorerPanel,
      openExplorerPanel: store.openExplorerPanel,
      setExplorerPanelWidth: store.setExplorerPanelWidth,
      closeUtilityPanel: store.closeUtilityPanel,
      openUtilityPanel: store.openUtilityPanel,
      setUtilityPanelWidth: store.setUtilityPanelWidth,
    }),
    [store]
  );

  return React.useMemo(() => [state, actions], [state, actions]);
}

/*
|------------------
| Internals
|------------------
*/

interface LayoutStoreState {
  utilityPanelOpen: boolean;
  utilityPanelType: LayoutUtilityPanelType | null;
  utilityPanelWidth: number;
  explorerPanelOpen: boolean;
  explorerPanelType: LayoutExplorerPanelType | null;
  explorerPanelWidth: number;
}

interface LayoutStoreActions {
  closeExplorerPanel: () => void;
  openExplorerPanel: (payload: {
    type: LayoutExplorerPanelType;
    width?: number;
  }) => void;
  setExplorerPanelWidth: (payload: { width: number }) => void;
  closeUtilityPanel: () => void;
  openUtilityPanel: (payload: {
    type: LayoutUtilityPanelType;
    width?: number;
  }) => void;
  setUtilityPanelWidth: (payload: { width: number }) => void;
}

interface LayoutStore extends LayoutStoreState, LayoutStoreActions {}

const useStore = create<LayoutStore>()(
  persist(
    (set) => ({
      explorerPanelOpen: false,
      explorerPanelType: 'explorer',
      explorerPanelWidth: 25,
      utilityPanelOpen: false,
      utilityPanelType: 'chat',
      utilityPanelWidth: 25,
      closeExplorerPanel: () => {
        set(() => ({
          explorerPanelOpen: false,
        }));
      },
      openExplorerPanel: (payload) => {
        set(() => ({
          explorerPanelType: payload.type,
          explorerPanelOpen: true,
          explorerPanelWidth: payload?.width ?? 25,
        }));
      },
      setExplorerPanelWidth: (payload) => {
        set(() => ({
          explorerPanelWidth: payload.width,
        }));
      },
      closeUtilityPanel: () => {
        set(() => ({
          utilityPanelOpen: false,
        }));
      },
      openUtilityPanel: (payload) => {
        set(() => ({
          utilityPanelOpen: true,
          utilityPanelWidth: payload?.width ?? 25,
        }));
      },
      setUtilityPanelWidth: (payload) => {
        set(() => ({
          utilityPanelWidth: payload.width,
        }));
      },
    }),
    {
      name: 'layout-store',
      storage: createJSONStorage(() => createZustandStorage('layout')),
    }
  )
);
